import {Inject, Injectable} from '@angular/core';
import {
    BlockHasBeenSeenAction,
    BlockQuestionsCompleteAction,
    BlockQuestionsIncompleteAction
} from '../+state/blocks/blocks.actions';
import {getElementId} from '../+state/elements/elements.reducer';
import {SurveyElementType} from '../enum/SurveyElementType';
import {VALUE_TYPES_ENUM, withValue} from '../utilities/with-value.function';
import {ISurveyQuestion} from '../../models/survey/ISurveyQuestion';
import {SurveyElement} from '../../models/survey/ISurveyElement';
import {getBlockId} from '../+state/blocks/blocks.reducer';
import {ISurveyBlock} from '../../models/survey/ISurveyBlock';
import {Dictionary} from '@ngrx/entity';
import {MultiSelectSurveyResponse, SurveyResponse} from '../../models/survey-responses/SurveyResponse.interface';
import {Store} from '@ngrx/store';
import {BlocksFacade} from '../+state/blocks/blocks.facade';
import {get} from 'lodash';
import {ResponsesFacade} from '../+state/responses/responses.facade';
import {MultiSelectQuestionGroup} from '../../models/survey-responses/MultiSelectQuestionGroup.interface';
import {SurveyContentService} from './survey-content.service';
import {ISurveyMultipartQuestion} from '@clients/member-direct/surveys';
import {ElementsFacade} from '../+state/elements/elements.facade';
import { isNotNullOrUndefined } from '@clients/shared/utilities';
import { ENV_SERVICE } from '@clients/shared/config';
import { MemberDirectEnvService } from '@clients/member-direct/survey-common';

@Injectable({
    providedIn: 'root'
})
export class BlockStateService {
    private questionTypes: SurveyElementType[] = [
        SurveyElementType.SURVEY_QUESTION,
        SurveyElementType.MULTIPART_QUESTION
    ];
    private isDevMode: boolean;

    constructor(
        @Inject(ENV_SERVICE) envService: MemberDirectEnvService,
        private store: Store<any>,
        private blocksFacade: BlocksFacade,
        private responsesFacade: ResponsesFacade,
        private elementsFacade: ElementsFacade,
        private surveyContentService: SurveyContentService
    ) {
        this.isDevMode = envService.env === "dev" || envService.env === "local";
    }

    /**
     * Given the complexity of the block state logic, this is our only public function.
     * This function checks the active and prior block and determines if the state of either
     * has changed given the responses to and existence of questions that a user may have
     * answered in a particular way or skipped.
     */
    public update() {
        this.processActiveBlock();
        this.processLastBlock();
    }

    /**
     * Check the currently-active block and see if we already know whether
     * its state should be changed.
     */
    private processActiveBlock() {
        const activeBlock: ISurveyBlock = this.blocksFacade.getActive();

        // completion blocks are always seen and completed right away
        if (activeBlock.completed) {
            const blockId = getBlockId(activeBlock);
            this.markBlockAsSeen(blockId);
            this.markBlockAsComplete(blockId);
        }
    }

    /**
     * Check the previously-active block to see if its state should change.
     */
    private processLastBlock() {
        // sanity check one
        const priorBlockId: string = this.blocksFacade.getPreviouslyActiveId();
        if (!priorBlockId) return;

        // sanity check two
        const priorBlock: ISurveyBlock = this.blocksFacade.get(priorBlockId);
        if (!priorBlock) return;

        // the last block should always been marked as seen since we
        // know the user has just been there.
        this.markBlockAsSeen(priorBlockId);
        // either mark the prior block complete or incomplete
        this.blockIsComplete(priorBlock)
            ? this.markBlockAsComplete(priorBlockId)
            : this.markBlockAsIncomplete(priorBlockId);
    }

    private blockIsComplete(block: ISurveyBlock): boolean {
        // pull a list of all of the questions in a block
        const elements: ISurveyQuestion<any>[] = this.surveyContentService.getElementsByType(
            block.content,
            this.questionTypes
        ) as ISurveyQuestion<any>[];
        if (!elements || !elements.length) return true;

        // get the list of all hidden question IDs
        const hidden = this.elementsFacade.getHidden();

        // get the list of all available question IDs that are not multi-select
        const questionIds: string[] = this.getBasicQuestionIds(elements, hidden);

        // get the list of all available multi-choice question IDs
        const multiChoiceQuestions: string[] = this.getMultiChoiceQuestionIds(elements, hidden);

        // build the multi-select question groups
        const multiSelectQuestionGroups: MultiSelectQuestionGroup[] = this.getMultiSelectQuestionGroups(elements, hidden);

        // if there are no simple question IDs and no multiselect questions, then the
        // block is complete
        /* istanbul ignore if */
        if ((!questionIds || !questionIds.length) && !multiSelectQuestionGroups.length) return true;

        // get the list of all logged responses
        const responses = this.responsesFacade.getEntities();
        const idsOfUserProvidedResponses: string[] = Object.keys(responses);
        const valuesOfUserProvidedResponses: SurveyResponse[] = Object.values(responses);

        // console log question completed or not and question hidden
        const allQuestionSeededMultiChoiceNotCompleted = valuesOfUserProvidedResponses
            .filter((ele: SurveyResponse) => !(multiChoiceQuestions.indexOf(ele.id) === -1))
            .filter((ele: SurveyResponse) => ele.value === false);
        const questionSeededMultiChoiceNotCompleted = allQuestionSeededMultiChoiceNotCompleted
            .map((ele: SurveyResponse) => {
                const newEle = elements.find(e => e.id === ele.id);
                return newEle ? { id: newEle.id, label: newEle.question.label } : ele;
            });

        const questionBasicCompleted = [];
        const questionBasicNotCompleted = elements
            .filter((ele: SurveyElement) => hidden.indexOf(ele.id) === -1)
            .filter(
                (ele: ISurveyQuestion<any> | ISurveyMultipartQuestion) =>
                    get(ele, 'question.type') !== SurveyElementType.MULTIPLE_SELECT
            )
            .filter((ele: SurveyElement) => {
                if (!(idsOfUserProvidedResponses.indexOf(ele.id) === -1)) {
                    if (get(ele, 'question.type') === SurveyElementType.MULTIPLE_CHOICE) {
                        if (!allQuestionSeededMultiChoiceNotCompleted.some((element: SurveyResponse) => element.id === ele.id)) {
                            questionBasicCompleted.push({ id: ele.id, label: ele.question.label })
                        }
                    } else {
                        questionBasicCompleted.push({ id: ele.id, label: ele.question.label })
                    }
                }
                return idsOfUserProvidedResponses.indexOf(ele.id) === -1;
            })
            .map((ele: SurveyElement) => ({ id: ele.id, label: ele.question.label }));
        const questionHidden = elements
            .filter((ele: SurveyElement) => !(hidden.indexOf(ele.id) === -1))
            .map((ele: SurveyElement) => ({ id: ele.id, label: ele.question.label }));
        const questionMultiSelectNotCompleted = [];
        const questionMultiSelectCompleted = multiSelectQuestionGroups.filter(
            (questionGroup: MultiSelectQuestionGroup) => {
                const checking = this.multiSelectQuestionHasBeenAnswered(questionGroup, responses);
                if (!checking) {
                    questionMultiSelectNotCompleted.push(questionGroup.questionId)
                }
                return checking;
            })
            .map((questionGroup: MultiSelectQuestionGroup) => {
                const element = elements.find((ele: SurveyElement) => ele.id === questionGroup.questionId);
                return { id: element.id, label: element.question.label }
            });
        const finalQuestionMultiSelectNotCompleted = questionMultiSelectNotCompleted.map((questionId: string) => {
            const element = elements.find((ele: SurveyElement) => ele.id === questionId);
            return element && { id: element.id, label: element.question.label }
        });

        if (this.isDevMode) {
            console.log('Question completed: ', [ ...questionBasicCompleted, ...questionMultiSelectCompleted]);
            console.log('Question not-completed: ', [ ...questionBasicNotCompleted, ...finalQuestionMultiSelectNotCompleted, ...questionSeededMultiChoiceNotCompleted]);
            console.log('Question hidden: ', questionHidden);
        }

        // check if basic and multi-select questions have been answered
        const allBasicQuestionsAreAnswered: boolean = this.allBasicQuestionsAreAnswered(
            questionIds,
            idsOfUserProvidedResponses
        );
        const allQuestionSeededMultiChoiceAnswerd: boolean = this.allQuestionSeededMultiChoiceAnswerd(
            multiChoiceQuestions,
            valuesOfUserProvidedResponses
        );
        const allMultiSelectQuestionsAreAnswered: boolean = this.allMultiSelectQuestionsAreAnswered(
            multiSelectQuestionGroups,
            responses
        );

        return allBasicQuestionsAreAnswered && allMultiSelectQuestionsAreAnswered && allQuestionSeededMultiChoiceAnswerd;
    }

    private getBasicQuestionIds(elements: SurveyElement[], hidden: string[]): string[] {
        return elements
            .filter((ele: SurveyElement) => hidden.indexOf(ele.id) === -1)
            .filter(
                (ele: ISurveyQuestion<any> | ISurveyMultipartQuestion) =>
                    get(ele, 'question.type') !== SurveyElementType.MULTIPLE_SELECT
            )
            .reduce((agg: string[], ele: ISurveyQuestion<any> | ISurveyMultipartQuestion) => {
                if (ele.type === SurveyElementType.MULTIPART_QUESTION) {
                    agg = agg.concat(this.getBasicQuestionIds(ele.data, hidden));
                } else {
                    agg.push(ele.id);
                }

                return agg;
            }, []);
    }

    private getMultiChoiceQuestionIds(elements: SurveyElement[], hidden: string[]): string[] {
        return elements
            .filter((ele: SurveyElement) => hidden.indexOf(ele.id) === -1)
            .filter(
                (ele: ISurveyQuestion<any> | ISurveyMultipartQuestion) =>
                    get(ele, 'question.type') === SurveyElementType.MULTIPLE_CHOICE
            )
            .map(
                (ele: ISurveyQuestion<any>): string => ele.id
            );
    }

    private getMultiSelectQuestionGroups(elements: SurveyElement[], hidden: string[]): MultiSelectQuestionGroup[] {
        return elements
            .filter((ele: SurveyElement) => hidden.indexOf(ele.id) === -1)
            .filter(
                (element: ISurveyQuestion<any>) => get(element, 'question.type') === SurveyElementType.MULTIPLE_SELECT
            )
            .map(
                (element: ISurveyQuestion<any>): MultiSelectQuestionGroup =>
                    this.parseMultiSelectQuestionGroupFromElement(element)
            );
    }

    private parseMultiSelectQuestionGroupFromElement(element: SurveyElement): MultiSelectQuestionGroup {
        const questionId: string = getElementId(element);
        const responseOptions = withValue(() => get(element, `question.data.options`), VALUE_TYPES_ENUM.ARRAY);
        const responseIds: string[] = withValue(
            () => responseOptions.map((option) => option.id),
            VALUE_TYPES_ENUM.ARRAY
        );

        return {
            questionId,
            responseIds
        };
    }

    /**
     * Checks all multiselect response groups to make sure at least one option from each
     * multiselect has been chosen indicating that the user has responded to all multiselect
     * questions by interacting with at least one option.
     */
    private allMultiSelectQuestionsAreAnswered(
        multiSelectQuestionGroups: MultiSelectQuestionGroup[],
        responses: Dictionary<SurveyResponse | MultiSelectSurveyResponse> = {}
    ): boolean {
        // if we don't actually have any multiselect groups then all are considered answered
        if (!multiSelectQuestionGroups || !multiSelectQuestionGroups.length) return true;

        const numberOfAnsweredMultiSelectGroups = multiSelectQuestionGroups.filter(
            (questionGroup: MultiSelectQuestionGroup) =>
                this.multiSelectQuestionHasBeenAnswered(questionGroup, responses)
        ).length;
        const totalNumberOfMultiSelectGroups = multiSelectQuestionGroups.length;

        return numberOfAnsweredMultiSelectGroups === totalNumberOfMultiSelectGroups;
    }

    private allBasicQuestionsAreAnswered(questionIds: string[], idsOfUserProvidedResponses: string[]): boolean {
        return questionIds.filter((id: string) => idsOfUserProvidedResponses.indexOf(id) === -1).length === 0;
    }

    private allQuestionSeededMultiChoiceAnswerd(questionIds: string[], seededResponses: SurveyResponse[]): boolean {
        return seededResponses
            .filter((ele: SurveyResponse) => !(questionIds.indexOf(ele.id) === -1))
            .filter((ele: SurveyResponse) => ele.value === false).length === 0;
    }

    private multiSelectQuestionHasBeenAnswered(
        questionGroup: MultiSelectQuestionGroup,
        responses: Dictionary<SurveyResponse | MultiSelectSurveyResponse>
    ): boolean {
        // if the question has no possible answered, then mark it as answered
        if (!questionGroup.responseIds.length) return true;

        let answered = false;
        for (const id of questionGroup.responseIds) {
            // if we find a response for an id, then the entire multi-select question group
            // can be considered answered. Currently this only checks if there's at least
            // one answer rather than considering validation.
            //
            // todo | support validation of min / max selections https://healthtel.atlassian.net/browse/SB-1006
            //
            if (this.multiSelectHasAnswer(id, responses)) {
                answered = true;
                break;
            }
        }

        return answered;
    }

    private multiSelectHasAnswer(
        id: string,
        responses: Dictionary<SurveyResponse | MultiSelectSurveyResponse>
    ): boolean {
        return responses[id] && responses[id].value === true;
    }

    private markBlockAsSeen(blockId: string): void {
        this.store.dispatch(new BlockHasBeenSeenAction(blockId));
    }

    private markBlockAsIncomplete(blockId: string): void {
        this.store.dispatch(new BlockQuestionsIncompleteAction(blockId));
    }

    private markBlockAsComplete(blockId: string): void {
        this.store.dispatch(new BlockQuestionsCompleteAction(blockId));
    }
}
